import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  ClipboardPaste,
  Loader2,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  MapPin,
  Music,
  ListChecks,
  Trash2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { runAiJob } from '../utils/aiJobs';
import ClientTextImportDialog from './ClientTextImportDialog';

const API = API_URL;

const TextParser = ({ onEventCreated, eventContext = null }) => {
  const [text, setText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [applyToCurrentEvent, setApplyToCurrentEvent] = useState(!!eventContext?.playlist_id);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const eventId = eventContext?.playlist_id;
  const eventName = eventContext?.nombre_evento;

  const processText = async () => {
    if (!text.trim()) {
      toast.error('Pega o escribe un texto primero');
      return;
    }

    // Modo "aplicar al evento abierto" → abre el diálogo de importación
    if (applyToCurrentEvent && eventId) {
      setImportDialogOpen(true);
      return;
    }

    setProcessing(true);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await runAiJob({
        startUrl: `${API}/dj-assistant/process-text/start`,
        statusBase: `${API}/dj-assistant/jobs`,
        body: { text: text.trim() },
        config: { headers: { Authorization: `Bearer ${token}` } },
      });

      setResults(response.data);

      if (response.data.events_created > 0 || response.data.tasks_created > 0) {
        toast.success(response.data.message);
        if (onEventCreated) onEventCreated();
      } else {
        toast.info('No se detectaron eventos en el texto');
      }
    } catch (error) {
      console.error('Error processing text:', error);
      const msg = error.response?.data?.detail || 'Error al procesar el texto';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const clearAll = () => {
    setText('');
    setResults(null);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-3 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2 text-slate-300">
          <ClipboardPaste className="h-5 w-5 text-teal-400" />
          <span className="font-medium text-sm">Pegar texto para crear evento/tarea</span>
        </div>

        <p className="text-xs text-slate-500">
          Pega texto de un email, WhatsApp o cualquier fuente. DJ Master interpretará la información y creará eventos y tareas automáticamente.
        </p>

        {/* Toggle: aplicar al evento abierto (solo si hay eventContext) */}
        {eventId && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-900/20 border border-emerald-700/40 rounded-lg">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-emerald-300">Aplicar al evento abierto</p>
                <p className="text-[10px] text-emerald-400/70 truncate">{eventName}</p>
              </div>
            </div>
            <Switch
              checked={applyToCurrentEvent}
              onCheckedChange={setApplyToCurrentEvent}
              data-testid="text-parser-apply-to-event-toggle"
            />
          </div>
        )}

        {/* Textarea */}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Ejemplo:\nSÁBADO 10 DE MAYO\nENLACE PEDRO - ANA\nBoda Civil a las 13.00\nBaile en Salón 1\nSesión 4 horas\nHorario de 19.00 a 00.00\nReunión novios martes 6 a las 10.00"}
          className="min-h-[180px] bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-600 text-sm resize-none"
          data-testid="text-parser-input"
          disabled={processing}
        />

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={processText}
            disabled={processing || !text.trim()}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="text-parser-submit"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analizando...
              </>
            ) : applyToCurrentEvent && eventId ? (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analizar para este evento
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Crear evento / tarea
              </>
            )}
          </Button>
          {(text || results) && (
            <Button
              onClick={clearAll}
              variant="outline"
              className="border-slate-700 text-slate-400 hover:bg-slate-800"
              data-testid="text-parser-clear"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-2 pt-2 border-t border-slate-700" data-testid="text-parser-results">
            {/* Summary */}
            <div className="flex items-center gap-2">
              {results.events_created > 0 || results.tasks_created > 0 ? (
                <Badge className="bg-green-600 text-xs">{results.message}</Badge>
              ) : (
                <Badge className="bg-amber-600 text-xs">No se detectaron eventos</Badge>
              )}
            </div>

            {/* Events created */}
            {results.events?.map((ev, i) => (
              <div key={i} className="p-3 bg-slate-800/80 border border-slate-700 rounded-lg space-y-1.5">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-400 shrink-0" />
                  <span className="text-sm font-medium text-white">{ev.nombre}</span>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {ev.fecha && (
                    <span className="text-slate-400">{ev.fecha}</span>
                  )}
                  {ev.lugar && (
                    <span className="flex items-center gap-1 text-slate-400">
                      <MapPin className="h-3 w-3" />{ev.lugar}
                    </span>
                  )}
                  {(ev.horario_inicio || ev.horario_fin) && (
                    <span className="flex items-center gap-1 text-slate-400">
                      <Clock className="h-3 w-3" />{ev.horario_inicio || '?'} - {ev.horario_fin || '?'}
                    </span>
                  )}
                  {ev.duracion_baile && (
                    <Badge className="bg-purple-700 text-[10px]">
                      <Music className="h-3 w-3 mr-1" />{ev.duracion_baile}
                    </Badge>
                  )}
                </div>

                {ev.servicios_raw?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {ev.servicios_raw.map((s, j) => (
                      <Badge key={j} className="bg-slate-700 text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Tasks created */}
            {results.tasks?.map((task, i) => (
              <div key={i} className="p-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span className="text-sm text-emerald-300">{task.title}</span>
                </div>
                <p className="text-xs text-emerald-400/70 mt-1">
                  {task.fecha} a las {task.hora}
                </p>
              </div>
            ))}

            {/* Errors */}
            {results.errors?.map((err, i) => (
              <div key={i} className="p-2 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                <span className="text-xs text-red-300">{err}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diálogo de importación al evento abierto */}
      {eventId && (
        <ClientTextImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          eventId={eventId}
          eventName={eventName}
          initialText={text}
          onApplied={() => {
            setImportDialogOpen(false);
            setText('');
            if (onEventCreated) onEventCreated();
          }}
        />
      )}
    </ScrollArea>
  );
};

export default TextParser;
